const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatPrice(item) {
  if (!item) return '';
  if (item.isFree === true) return 'Free';
  const code = item.currency || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(Number(item.price) || 0);
  } catch {
    return currencyFmt.format(Number(item.price) || 0);
  }
}

export function formatMoney(value, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  } catch {
    return currencyFmt.format(Number(value) || 0);
  }
}

/** Previous price for strikethrough — matches mobile (shown even when Free). */
export function getPreviousPrice(item) {
  if (!item) return null;
  const prev = Number(item.previousPrice);
  const price = Number(item.price) || 0;
  if (!Number.isFinite(prev) || prev <= 0) return null;
  if (prev === price) return null;
  return prev;
}

const FRESHNESS_WINDOW_MS = 48 * 60 * 60 * 1000;

function timestampMs(value) {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (value?.toMillis) return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value?._seconds === 'number') return value._seconds * 1000;
  return 0;
}

export function createdAtMs(item) {
  if (!item) return 0;
  if (item.createdAtMs) return Number(item.createdAtMs) || 0;
  return timestampMs(item.createdAt);
}

export function contentUpdatedAtMs(item) {
  if (!item) return 0;
  if (item.contentUpdatedAtMs) return Number(item.contentUpdatedAtMs) || 0;
  return timestampMs(item.contentUpdatedAt);
}

export function priceDroppedAtMs(item) {
  if (!item) return 0;
  if (item.priceDroppedAtMs) return Number(item.priceDroppedAtMs) || 0;
  return timestampMs(item.priceDroppedAt);
}

function effectiveListingPrice(item) {
  if (!item) return 0;
  if (item.isFree === true) return 0;
  return Number(item.price) || 0;
}

/** True when previousPrice is higher than the current effective price (including Free). */
export function isPriceDropListing(item) {
  if (!item) return false;
  const prev = Number(item.previousPrice);
  if (!Number.isFinite(prev) || prev <= 0) return false;
  return prev > effectiveListingPrice(item);
}

/**
 * Highest-priority freshness badge for a listing.
 * Priority: price_drop → just_listed → just_updated.
 * @returns {'price_drop'|'just_listed'|'just_updated'|null}
 */
export function listingFreshnessBadge(item, nowMs = Date.now()) {
  if (!item) return null;
  const created = createdAtMs(item);
  const justListed = created > 0 && nowMs - created < FRESHNESS_WINDOW_MS;

  if (isPriceDropListing(item)) {
    const droppedAt = priceDroppedAtMs(item) || contentUpdatedAtMs(item) || timestampMs(item.updatedAt);
    if (droppedAt > 0 && nowMs - droppedAt < FRESHNESS_WINDOW_MS) {
      return 'price_drop';
    }
  }

  if (justListed) return 'just_listed';

  const contentUpdated = contentUpdatedAtMs(item);
  if (contentUpdated > 0 && nowMs - contentUpdated < FRESHNESS_WINDOW_MS) {
    // Ignore create-time stamps that match createdAt
    if (!created || contentUpdated - created > 60_000) {
      return 'just_updated';
    }
  }

  return null;
}

export function listingFreshnessLabel(kind) {
  switch (kind) {
    case 'price_drop':
      return 'Price Drop';
    case 'just_listed':
      return 'Just Listed';
    case 'just_updated':
      return 'Just Updated';
    default:
      return '';
  }
}

/** @deprecated Use listingFreshnessBadge — kept for call sites during migration. */
export function isNewListing(item) {
  return listingFreshnessBadge(item) === 'just_listed';
}

export function isHighDemand(item) {
  return (item?.saveCount || 0) >= 20;
}

export function relativeTime(value) {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return 'Just now';
  const hours = Math.max(0, Math.round((Date.now() - date.getTime()) / 36e5));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function coverPhoto(item) {
  const photos = item?.photos || [];
  if (!photos.length) return '';
  const idx = Math.min(Math.max(item.mainPhotoIndex || 0, 0), photos.length - 1);
  return photos[idx] || '';
}

export function orderedPhotos(item) {
  const photos = item?.photos || [];
  if (!photos.length) return [];
  const main = coverPhoto(item);
  const idx = Math.min(Math.max(item.mainPhotoIndex || 0, 0), photos.length - 1);
  return [main, ...photos.filter((_, i) => i !== idx)];
}

export function sellerDisplayName(seller, fallback = 'Sosello member') {
  if (!seller) return fallback;
  const name = (seller.displayName || seller.name || '').trim();
  if (name) return name;
  const email = (seller.email || '').trim();
  if (email.includes('@')) return email.split('@')[0];
  return fallback;
}

export function initials(name, fallback = 'S') {
  const text = (name || '').trim();
  if (!text) return fallback;
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return text.slice(0, 1).toUpperCase();
}

export function shareUrl(listingId) {
  // Always share the public sosello.com/l/ page: it has Open Graph metadata,
  // Product structured data, and is indexable — app.sosello.com is auth-gated.
  const base = (process.env.REACT_APP_SHARE_BASE_URL || 'https://sosello.com/l').replace(/\/$/, '');
  return `${base}/${listingId}`;
}

export function listingStatusLabel(status) {
  switch (status) {
    case 'draft': return 'Draft';
    case 'pending': return 'Pending review';
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    case 'sold': return 'Sold';
    case 'archived': return 'Archived';
    default: return status || '';
  }
}

export function toE164(raw, defaultCountryCode = '1') {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, '');
  const hasPlus = trimmed.startsWith('+');
  if (hasPlus && digits.length >= 8 && digits.length <= 15) return `+${digits}`;
  if (digits.length === 10) return `+${defaultCountryCode}${digits}`;
  if (digits.length === 11 && digits.startsWith(defaultCountryCode)) return `+${digits}`;
  if (!hasPlus && digits.length >= 8 && digits.length <= 15) return `+${digits}`;
  return null;
}
